"use client";

import { useSession } from "@/core/session";
import type { Sessao } from "@/types";

export const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api/v1";

export class ApiError extends Error {
  status: number;
  codigo?: string;
  constructor(status: number, message: string, codigo?: string) {
    super(message);
    this.status = status;
    this.codigo = codigo;
  }
}

export type Query = Record<string, string | number | boolean | null | undefined | string[]>;

interface Options {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Query;
  auth?: boolean;
}

function buildUrl(path: string, query?: Query) {
  const url = new URL(API_BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) v.forEach((x) => url.searchParams.append(k, x));
      else url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

let renovacao: Promise<boolean> | null = null;

/**
 * A API grava "rf_tem_sessao=1" junto do cookie de sessão (httpOnly, ilegível aqui).
 * Sem ele não há o que renovar. Com a API em outra origem o aviso não é visível: tenta sempre.
 */
function talvezLogado() {
  if (/^https?:\/\//.test(API_BASE) && !API_BASE.startsWith(window.location.origin)) return true;
  return /(?:^|;\s*)rf_tem_sessao=1/.test(document.cookie);
}

/**
 * Novo token de acesso a partir do cookie httpOnly de sessão (POST /auth/refresh).
 * Uma única chamada por vez: requisições que recebem 401 juntas esperam a mesma renovação.
 */
export function renovarSessao(): Promise<boolean> {
  if (!talvezLogado()) return Promise.resolve(false);
  renovacao ??= (async () => {
    try {
      const res = await fetch(buildUrl("/auth/refresh"), {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return false;
      useSession.getState().renovar((await res.json()) as Sessao);
      return true;
    } catch {
      return false;
    } finally {
      renovacao = null;
    }
  })();
  return renovacao;
}

export async function api<T>(path: string, opts: Options = {}, tentativa = 0): Promise<T> {
  const { token, empresaId } = useSession.getState();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.auth !== false && token) headers.Authorization = `Bearer ${token}`;
  if (empresaId) headers["X-Empresa-Id"] = empresaId;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? "GET",
      headers,
      credentials: "include",
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new ApiError(0, "Sem conexão");
  }

  if (res.status === 401 && opts.auth !== false) {
    // Token de acesso curto venceu: renova pelo cookie e repete uma vez
    if (tentativa === 0 && token && (await renovarSessao())) return api<T>(path, opts, 1);
    useSession.getState().expire();
    throw new ApiError(401, "Sessão expirada");
  }

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // Ex: página HTML de erro do proxy (502)
    if (res.ok) throw new ApiError(res.status, "Resposta inválida do servidor");
  }
  if (!res.ok) {
    if (res.status === 402 && (data?.codigo === "ASSINATURA_INATIVA" || data?.codigo === "ASSINATURA_INADIMPLENTE")) {
      // INATIVA: o app leva para a assinatura; INADIMPLENTE: consulta liberada, emissão bloqueada
      const { conta } = useSession.getState();
      const acesso = data.codigo === "ASSINATURA_INATIVA" ? "BLOQUEADO" : "LEITURA";
      if (conta && conta.acesso !== acesso) useSession.setState({ conta: { ...conta, acesso } });
    }
    const mensagem = data?.mensagem ?? data?.motivo ?? (res.status >= 500 ? "Servidor indisponível" : "Falha na requisição");
    throw new ApiError(res.status, Array.isArray(mensagem) ? mensagem.join(", ") : mensagem, data?.codigo ?? undefined);
  }
  return data as T;
}

export function errorMessage(e: unknown) {
  // Códigos de rejeição (SEFAZ, provedores: "539", "AUTH-401") ajudam o suporte e aparecem;
  // códigos internos da API ("LIMITE_PLANO") servem só para o front decidir o que fazer
  if (e instanceof ApiError) return e.codigo && /\d/.test(e.codigo) ? `${e.codigo} · ${e.message}` : e.message;
  if (e instanceof Error) return e.message;
  return "Erro inesperado";
}

/**
 * Baixa um arquivo da API (XML, PDF) com a sessão atual e salva com o nome do Content-Disposition.
 * 401 renova a sessão e tenta de novo uma vez.
 */
export async function baixarArquivo(path: string, tentativa = 0): Promise<void> {
  const { token, empresaId } = useSession.getState();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (empresaId) headers["X-Empresa-Id"] = empresaId;
  let res: Response;
  try {
    res = await fetch(buildUrl(path), { headers, credentials: "include" });
  } catch {
    throw new ApiError(0, "Sem conexão");
  }
  if (res.status === 401 && tentativa === 0 && token && (await renovarSessao())) return baixarArquivo(path, 1);
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new ApiError(res.status, d?.mensagem ?? d?.message ?? `Falha ao baixar (${res.status})`, d?.codigo);
  }
  const nome = /filename="?([^";]+)"?/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? path.split("/").pop() ?? "arquivo";
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
